import { Button } from "@/components/ui/button";
import { ArrowDownToLine } from "lucide-react";
import { Block, HiVis } from "@/types/editor/blocks";
import { characterShapes, Characters } from "@/config/font";

interface DownloadDesignProps {
  debugMode?: boolean;
  hiVis: HiVis | null;
}

// Standalone generateSVG function for external use
export const generateHiVisSVG = async (hiVis: HiVis): Promise<string> => {
  if (!hiVis?.design?.blocks || hiVis.design.blocks.length === 0) return "";

  // Add SCALE constant to match vis-editor
  const SCALE = 3.779528; // 1mm = 3.779528 pixels (96 DPI)

  const blocks = hiVis.design.blocks;
  const useNaturalWidth = hiVis.naturalWidth || false;
  const baseMaterial = { color: blocks[0]?.color || "#FFFFFF" }; // Use first block's color as base material
  const global_gap = hiVis.design.global_gap || 0;
  // Get gap array from hiVis if available
  const gap = hiVis.gap || 0;

  // Function to calculate natural width ratio
  const getNaturalWidthRatio = (character: string) => {
    if (!character) return 1;
    const char = character.toLowerCase();
    if (char === " ") return 0.5; // Space is half width
    if (char === ".") return 0.2; // Dot is very narrow
    if (char === "-") return 0.5; // Dash is half width

    const shape = characterShapes[char as Characters];
    if (!shape) return 1;

    const [, , vbWidth, vbHeight] = shape.viewBox.split(" ").map(Number);
    return vbWidth / vbHeight;
  };

  // Function to handle color conversion
  const getBlockColor = (color: string | undefined) => {
    // Explicitly handle black
    if (color?.toLowerCase() === "#000000") {
      return "#000000";
    } else {
      return baseMaterial.color;
    }
  };

  // Function to render floating blocks to SVG
  const renderFloatingBlocksToSVG = (hiVis: HiVis, width: number): string => {
    if (
      !hiVis?.design?.floating_blocks ||
      hiVis.design.floating_blocks.length === 0
    ) {
      return "";
    }

    const elements: string[] = [];

    // Get the main design dimensions for scaling calculations
    const mainDesignWidth = hiVis.design.blocks[0]?.width || 200;

    // Match vis-editor scaling: width / (mainDesignWidth * SCALE)
    // The vis-editor uses SCALE factor to convert mm to pixels, we need to account for this
    const scaleFactor = width / (mainDesignWidth * SCALE);

    hiVis.design.floating_blocks.forEach((floatingBlock) => {
      // Apply the same scaling to floating block dimensions and position as used for main design
      // Match vis-editor calculation: floatingBlock.width * SCALE * scaleFactor
      const scaledWidth = floatingBlock.width * SCALE * scaleFactor;
      const scaledHeight = floatingBlock.height * SCALE * scaleFactor;
      const scaledX = floatingBlock.x * SCALE * scaleFactor;
      const scaledY = floatingBlock.y * SCALE * scaleFactor;

      // Create a group for the floating block to ensure it renders on top
      elements.push(`<g opacity="1.0">`);

      // Render the floating block with scaled dimensions
      const floatingBlockSVG = renderBlockToSVG(
        floatingBlock,
        scaledX,
        scaledY,
        scaledWidth,
        scaledHeight,
        0, // depth = 0 to ensure floating blocks render properly
        hiVis,
        floatingBlock.global_gap || global_gap, // Use floating block's own global gap or fallback to design global gap
        floatingBlock.naturalWidth !== undefined
          ? floatingBlock.naturalWidth
          : useNaturalWidth, // Use floating block's naturalWidth setting or fallback to hiVis setting
        true, // isFloatingBlock flag
        baseMaterial.color // Floating blocks start with base material color as parent
      );

      elements.push(floatingBlockSVG);
      elements.push(`</g>`);
    });

    return elements.join("\n");
  };

  // Helper function to apply color override to SVG content (matching vis-editor exactly)
  const applySvgColorOverride = (
    svgContent: string,
    baseMaterialColor: string,
    inverted: boolean
  ): string => {
    // Determine the target color based on inverted flag
    const targetColor = inverted ? baseMaterialColor : "#000000";

    try {
      // Use DOMParser for robust XML manipulation (same as vis-editor)
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgContent, "image/svg+xml");

      const walker = doc.createTreeWalker(
        doc.documentElement,
        NodeFilter.SHOW_ELEMENT
      );
      let node: Element | null = walker.currentNode as Element;
      while (node) {
        if (node.tagName.toLowerCase() !== "defs") {
          // 1. presentation attribute
          node.removeAttribute("fill");
          node.setAttribute("fill", targetColor);

          // 2. inline style attribute
          if (node.hasAttribute("style")) {
            const style = node.getAttribute("style")!;
            const updated = style.replace(
              /fill\s*:\s*[^;]+/gi,
              `fill:${targetColor}`
            );
            node.setAttribute("style", updated);
          }
        }
        node = walker.nextNode() as Element | null;
      }

      // 3. <style> blocks containing CSS rules
      const styleTags = doc.querySelectorAll("style");
      styleTags.forEach((styleEl) => {
        if (styleEl.textContent) {
          styleEl.textContent = styleEl.textContent.replace(
            /fill\s*:\s*#[0-9a-fA-F]{3,6}/gi,
            `fill:${targetColor}`
          );
        }
      });

      // serialize back to string
      const serializer = new XMLSerializer();
      return serializer.serializeToString(doc);
    } catch {
      // Fallback to simple regex (kept minimal to avoid malformed tags)
      return svgContent.replace(/fill=".*?"/gi, `fill="${targetColor}"`);
    }
  };

  // Function to render SVG overlay designs to SVG
  const renderSvgOverlaysToSVG = async (
    hiVis: HiVis,
    width: number
  ): Promise<string> => {
    if (!hiVis.design.designs || hiVis.design.designs.length === 0) {
      return "";
    }

    const elements: string[] = [];

    // Get the main design dimensions for scaling calculations
    const mainDesignWidth = hiVis.design.blocks[0]?.width || 200;

    // Calculate the scale factor to match vis-editor
    const scaleFactor = width / (mainDesignWidth * SCALE);

    // Process each overlay design with proper async handling
    for (let index = 0; index < hiVis.design.designs.length; index++) {
      const overlayDesign = hiVis.design.designs[index];

      // Apply scaling like vis-editor: overlayDesign.x * SCALE * scaleFactor
      const scaledX = overlayDesign.x * SCALE * scaleFactor;
      const scaledY = overlayDesign.y * SCALE * scaleFactor;
      const scaledWidth = overlayDesign.width * SCALE * scaleFactor;
      const scaledHeight = overlayDesign.height * SCALE * scaleFactor;
      const inverted = overlayDesign.inverted || false;

      // Handle both URL and raw SVG data (like vis-editor does)
      const svgContent = overlayDesign.svg;
      let processedSvg: string;

      try {
        // Fetch and process SVG content (matching vis-editor approach)
        if (svgContent.startsWith("http")) {
          // Fetch external SVG
          const response = await fetch(svgContent);
          if (!response.ok) {
            throw new Error(`Failed to fetch SVG: ${response.statusText}`);
          }
          const fetchedSvg = await response.text();
          processedSvg = applySvgColorOverride(
            fetchedSvg,
            baseMaterial.color,
            inverted
          );
        } else if (svgContent.startsWith("data:")) {
          // Decode data URL
          const [, base64] = svgContent.split(",", 2);
          const decodedSvg = atob(base64);
          processedSvg = applySvgColorOverride(
            decodedSvg,
            baseMaterial.color,
            inverted
          );
        } else {
          // Raw SVG content
          processedSvg = applySvgColorOverride(
            svgContent,
            baseMaterial.color,
            inverted
          );
        }

        // Extract viewBox or determine scale from the processed SVG
        let svgScale = 1;
        const viewBoxMatch = processedSvg.match(
          /viewBox\s*=\s*["'][^"']*?\s+(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)\s*["']/
        );
        const widthMatch = processedSvg.match(
          /width\s*=\s*["']?(\d+(?:\.\d+)?)/
        );
        const heightMatch = processedSvg.match(
          /height\s*=\s*["']?(\d+(?:\.\d+)?)/
        );

        if (viewBoxMatch) {
          const viewBoxWidth = parseFloat(viewBoxMatch[1]);
          const viewBoxHeight = parseFloat(viewBoxMatch[2]);
          svgScale = Math.min(
            scaledWidth / viewBoxWidth,
            scaledHeight / viewBoxHeight
          );
        } else if (widthMatch && heightMatch) {
          const originalWidth = parseFloat(widthMatch[1]);
          const originalHeight = parseFloat(heightMatch[1]);
          svgScale = Math.min(
            scaledWidth / originalWidth,
            scaledHeight / originalHeight
          );
        } else {
          // Fallback: scale to fit the allocated dimensions
          svgScale = Math.min(scaledWidth / 100, scaledHeight / 100);
        }

        // Remove the outer SVG wrapper if it exists to get inner content
        const svgMatch = processedSvg.match(/<svg[^>]*>([\s\S]*)<\/svg>/);
        const innerSvg = svgMatch ? svgMatch[1] : processedSvg;

        // Inline the SVG content with proper scaling
        elements.push(`
          <g transform="translate(${scaledX}, ${scaledY})">
            <g transform="scale(${svgScale})">
              ${innerSvg}
            </g>
          </g>
        `);
      } catch (error) {
        console.error(`Failed to process SVG overlay ${index}:`, error);
        // Skip rendering failed SVG overlays silently
      }
    }

    return elements.join("\n");
  };

  // Function to render a block to SVG
  const renderBlockToSVG = (
    block: Block,
    x: number,
    y: number,
    width: number,
    height: number,
    depth: number = 0,
    hiVis: HiVis,
    blockGlobalGap?: number,
    blockUseNaturalWidth?: boolean,
    isFloatingBlock: boolean = false,
    parentContainerColor?: string
  ): string => {
    const elements: string[] = [];

    // Use block-specific settings if provided, otherwise use hiVis settings
    const effectiveGlobalGap =
      blockGlobalGap !== undefined ? blockGlobalGap : global_gap;
    const effectiveUseNaturalWidth =
      blockUseNaturalWidth !== undefined
        ? blockUseNaturalWidth
        : useNaturalWidth;

    // Calculate the actual scale factor applied to this block for rendering
    // The gaps should be proportional to how the block has been scaled
    const scaleX = width / (block.width * SCALE);
    const scaleY = height / (block.height * SCALE);
    const renderScale = Math.min(scaleX, scaleY); // Use the smaller scale to maintain aspect ratio

    // Transparent background rules (matching vis-editor exactly):
    // 1. Individual character blocks (always transparent)
    // 2. Stacked containers (verticalStack) – act as character containers
    // All other containers should render their background colour
    const shouldBeTransparent =
      block.character !== undefined ||
      (block.isContainer && block.verticalStack === true);

    if (!shouldBeTransparent) {
      // If corner radius is specified and block is a container, draw rounded rectangle
      if (block.cornerRadius && block.isContainer) {
        const radius = block.cornerRadius * SCALE * renderScale;

        elements.push(
          `<rect x="${x}" y="${y}" width="${width}" height="${height}" rx="${radius}" ry="${radius}" fill="${getBlockColor(block.color)}" />`
        );
      } else {
        elements.push(
          `<rect x="${x}" y="${y}" width="${width}" height="${height}" fill="${getBlockColor(block.color)}" />`
        );
      }
    }

    if (block.blocks && block.blocks.length > 0) {
      // Special handling for vertical stacked blocks
      if (block.verticalStack) {
        const totalBlocks = block.blocks.length;
        let currentY = y;

        // Calculate total vertical gaps with proper scaling
        const totalVerticalGap = block.blocks.reduce((sum, b, i) => {
          if (i < totalBlocks - 1) {
            return sum + (b.gap || 0);
          }
          return sum;
        }, 0);

        // Calculate available height for blocks with properly scaled gaps
        const availableHeight = height - totalVerticalGap * SCALE * renderScale;
        const blockHeight = availableHeight / totalBlocks;

        // Handle vertical stacking of characters
        block.blocks.forEach((childBlock) => {
          // For vertical stack, width can be adjusted to maintain proportions
          let childWidth = width;
          const childHeight = blockHeight;

          // If useNaturalWidth is true, we'll center the character horizontally
          if (effectiveUseNaturalWidth && childBlock.character) {
            // Use the pre-calculated width from the block if available, otherwise calculate
            let stretchedWidth;
            if (childBlock.width && childBlock.width > 0) {
              // Use the pre-calculated stretched width from hivis-manager (convert mm to SVG units)
              stretchedWidth = childBlock.width * SCALE * renderScale;
              // Ensure it doesn't exceed the container width
              stretchedWidth = Math.min(stretchedWidth, width);
            } else {
              // Fallback to calculating stretched width
              const naturalRatio = getNaturalWidthRatio(childBlock.character);
              const naturalWidth = childHeight * naturalRatio;
              stretchedWidth = naturalWidth;
              if (width > naturalWidth) {
                const extraSpace = width - naturalWidth;
                stretchedWidth = naturalWidth + extraSpace * 0.75;
              }
              // Cap stretched width to container width only for calculated widths
              stretchedWidth = Math.min(stretchedWidth, width);
            }

            const horizontalPadding = (width - stretchedWidth) / 2;
            childWidth = stretchedWidth;

            elements.push(
              renderBlockToSVG(
                childBlock,
                x + horizontalPadding,
                currentY,
                childWidth,
                childHeight,
                depth + 1,
                hiVis,
                effectiveGlobalGap,
                effectiveUseNaturalWidth,
                isFloatingBlock,
                getBlockColor(block.color) // Pass current container color as parent
              )
            );
          } else {
            // Render at full width if not using natural width
            elements.push(
              renderBlockToSVG(
                childBlock,
                x,
                currentY,
                width,
                childHeight,
                depth + 1,
                hiVis,
                effectiveGlobalGap,
                effectiveUseNaturalWidth,
                isFloatingBlock,
                getBlockColor(block.color) // Pass current container color as parent
              )
            );
          }

          // Move down for next character, adding vertical gap if needed with proper scaling
          currentY += childHeight + (childBlock.gap || 0) * SCALE * renderScale;
        });

        return elements.join("\n");
      }

      const isVertical = width < height;
      const availableSpace = isVertical ? height : width;

      // Separate blocks by type for proper distribution
      const regularBlocks = block.blocks.filter(
        (b) => !b.isContainer || (b.isContainer && !b.verticalStack)
      );
      const stackedContainers = block.blocks.filter(
        (b) => b.isContainer && b.verticalStack
      );

      // Count effective blocks for distribution (each stacked container counts as one block)
      const effectiveBlockCount =
        regularBlocks.length + stackedContainers.length;

      // Calculate total gaps using the gap array if available with proper scaling
      const totalGaps = block.blocks.reduce((sum, b, i) => {
        if (i < block.blocks!.length - 1) {
          // Use the gap value directly from the block if available,
          // otherwise fallback to gap array or global gap
          const gapValue =
            b.gap !== undefined
              ? b.gap
              : Array.isArray(gap) && gap[i] !== undefined
                ? gap[i]
                : b.gapLocked
                  ? effectiveGlobalGap
                  : 0;

          return sum + gapValue;
        }
        return sum;
      }, 0);

      const spaceForBlocks = availableSpace - totalGaps * SCALE * renderScale;

      // Calculate total natural ratio if needed
      const totalNaturalRatio =
        effectiveUseNaturalWidth && !isVertical
          ? block.blocks.reduce((sum, b) => {
              if (b.isContainer) {
                if (b.verticalStack) {
                  // For stacked containers, get the wider character's natural ratio
                  if (b.blocks && b.blocks.length >= 2) {
                    const firstRatio = getNaturalWidthRatio(
                      b.blocks[0].character || ""
                    );
                    const secondRatio = getNaturalWidthRatio(
                      b.blocks[1].character || ""
                    );
                    return sum + Math.max(firstRatio, secondRatio);
                  }
                }
                return sum + 1;
              }
              return sum + getNaturalWidthRatio(b.character || "");
            }, 0)
          : effectiveBlockCount;

      let currentX = x;
      let currentY = y;

      // Process all blocks
      for (let index = 0; index < block.blocks.length; index++) {
        const childBlock = block.blocks[index];
        let childX: number;
        let childY: number;
        let childWidth: number;
        let childHeight: number;

        // Handle stacked containers specifically
        if (childBlock.isContainer && childBlock.verticalStack) {
          if (isVertical) {
            // For vertical layouts, stacked containers take full width
            childWidth = width;
            // Calculate height based on position in container
            const blockSize = spaceForBlocks / effectiveBlockCount;
            childHeight = blockSize;
            childX = x;
            childY = currentY;
            currentY += childHeight;
          } else {
            // For horizontal layouts, calculate width based on distribution
            if (effectiveUseNaturalWidth) {
              // Get natural ratio for the stacked container
              let stackRatio = 1;
              if (childBlock.blocks && childBlock.blocks.length > 0) {
                // Use max ratio of any characters in the stack
                stackRatio = childBlock.blocks.reduce((max, stackedChild) => {
                  if (stackedChild.character) {
                    const charRatio = getNaturalWidthRatio(
                      stackedChild.character
                    );
                    return Math.max(max, charRatio);
                  }
                  return max;
                }, 0.5);
              }
              childWidth = (spaceForBlocks * stackRatio) / totalNaturalRatio;
            } else {
              // Uniform distribution
              childWidth = spaceForBlocks / effectiveBlockCount;
            }
            childHeight = height;
            childX = currentX;
            childY = y;
          }

          // Render the stacked container
          elements.push(
            renderBlockToSVG(
              childBlock,
              childX,
              childY,
              childWidth,
              childHeight,
              depth + 1,
              hiVis,
              effectiveGlobalGap,
              effectiveUseNaturalWidth,
              isFloatingBlock,
              getBlockColor(block.color) // Pass current container color as parent
            )
          );

          // Update currentX position for next block
          if (!isVertical) {
            currentX += childWidth;
          }

          // Add gap after this block if it's not the last one
          if (index < block.blocks.length - 1) {
            // For stacked containers, use the gap stored on the stacked container itself
            const gapValue =
              childBlock.gap !== undefined
                ? childBlock.gap
                : Array.isArray(gap) && index < gap.length
                  ? gap[index]
                  : childBlock.gapLocked
                    ? effectiveGlobalGap
                    : 0;

            if (gapValue > 0) {
              if (isVertical) {
                currentY += gapValue * SCALE * renderScale;
              } else {
                currentX += gapValue * SCALE * renderScale;
              }
            }
          }

          continue; // Skip to next block since we've handled this one
        }

        if (childBlock.isContainer) {
          // Use container's actual dimensions
          childWidth = (childBlock.width / block.width) * width;
          childHeight = (childBlock.height / block.height) * height;

          // Center the container
          const blocks = block.blocks;
          const allocatedWidth = isVertical ? width : width / blocks.length;
          const allocatedHeight = isVertical ? height / blocks.length : height;

          // Calculate total gaps before this block
          const gapsBefore = blocks.slice(0, index).reduce((sum, _, i) => {
            const gapValue =
              Array.isArray(gap) && gap[i] !== undefined
                ? gap[i]
                : blocks[i].gapLocked
                  ? effectiveGlobalGap
                  : (blocks[i].gap ?? 0);
            return sum + gapValue;
          }, 0);

          const allocatedX = isVertical
            ? x
            : x + index * allocatedWidth + gapsBefore * SCALE * renderScale;
          const allocatedY = isVertical
            ? y + index * allocatedHeight + gapsBefore * SCALE * renderScale
            : y;

          childX = allocatedX + (allocatedWidth - childWidth) / 2;
          childY = allocatedY + (allocatedHeight - childHeight) / 2;
        } else {
          // For character blocks
          if (effectiveUseNaturalWidth && !isVertical) {
            // Calculate width based on natural ratio
            const naturalRatio = getNaturalWidthRatio(
              childBlock.character || ""
            );

            // Calculate proportional width based on natural ratio
            const blockWidth =
              (spaceForBlocks * naturalRatio) / totalNaturalRatio;

            childX = currentX;
            childY = y;
            childWidth = blockWidth;
            childHeight = height;

            // Update currentX for next character
            currentX += blockWidth;

            // Add gap after this character if it's not the last one
            if (index < block.blocks.length - 1) {
              // Use the gap stored directly on the character block
              const gapValue =
                childBlock.gap !== undefined
                  ? childBlock.gap
                  : Array.isArray(gap) && index < gap.length
                    ? gap[index]
                    : childBlock.gapLocked
                      ? effectiveGlobalGap
                      : 0;

              // Add the gap with proper scaling
              currentX += gapValue * SCALE * renderScale;
            }
          } else {
            // Use uniform distribution
            const blocks = block.blocks;
            const nonStackedBlocks = blocks.filter(
              (b) => !b.isContainer || (b.isContainer && !b.verticalStack)
            ).length;
            const stackedBlocks = blocks.filter(
              (b) => b.isContainer && b.verticalStack
            ).length;

            // Total effective blocks for sizing
            const effectiveCount = nonStackedBlocks + stackedBlocks;
            // Ensure blockSize doesn't go negative when gaps consume all space
            const blockSize = Math.max(0, spaceForBlocks / effectiveCount);

            if (isVertical) {
              childX = x;
              childY = currentY;

              // For vertical layouts, if using natural width, calculate width based on character ratio
              if (effectiveUseNaturalWidth && childBlock.character) {
                childHeight = blockSize;
                const naturalRatio = getNaturalWidthRatio(childBlock.character);
                // Calculate width based on natural ratio (height * ratio) to maintain proportions
                const naturalWidth = childHeight * naturalRatio;

                // If natural width is less than container width, center it horizontally
                if (naturalWidth < width) {
                  childWidth = naturalWidth;
                  // Center the character horizontally
                  childX = x + (width - childWidth) / 2;
                } else {
                  // If natural width would exceed container width, cap it
                  childWidth = width;
                }
              } else {
                // Default behavior for non-natural width mode
                childWidth = width;
                childHeight = blockSize;
              }

              currentY += blockSize;
              if (index < blocks.length - 1) {
                // Use the gap stored directly on the character block
                const gapValue =
                  childBlock.gap !== undefined
                    ? childBlock.gap
                    : Array.isArray(gap) && index < gap.length
                      ? gap[index]
                      : childBlock.gapLocked
                        ? effectiveGlobalGap
                        : 0;

                currentY += gapValue * SCALE * renderScale;
              }
            } else {
              childX = currentX;
              childY = y;
              childWidth = blockSize;
              childHeight = height;
              currentX += blockSize;
              if (index < blocks.length - 1) {
                // Use the gap stored directly on the character block
                const gapValue =
                  childBlock.gap !== undefined
                    ? childBlock.gap
                    : Array.isArray(gap) && index < gap.length
                      ? gap[index]
                      : childBlock.gapLocked
                        ? effectiveGlobalGap
                        : 0;

                currentX += gapValue * SCALE * renderScale;
              }
            }
          }
        }

        elements.push(
          renderBlockToSVG(
            childBlock,
            childX,
            childY,
            childWidth,
            childHeight,
            depth + 1,
            hiVis,
            effectiveGlobalGap,
            effectiveUseNaturalWidth,
            isFloatingBlock,
            getBlockColor(block.color) // Pass current container color as parent
          )
        );
      }
    } else if (block.character) {
      // Contrast rule (matching vis-editor exactly): if the background (parentContainerColor) equals the base-material colour, draw glyphs in black.
      // Otherwise draw glyphs in the current base-material colour for visibility on dark/other backgrounds.
      let textColor: string;
      if (parentContainerColor && parentContainerColor === baseMaterial.color) {
        textColor = "#000000";
      } else {
        textColor = baseMaterial.color || "#000000";
      }

      // Special handling for dot and dash characters
      if (
        block.character === "." ||
        block.character === "-" ||
        block.character === " "
      ) {
        if (block.character === ".") {
          const [, , vbWidth, vbHeight] = characterShapes.dot.viewBox
            .split(" ")
            .map(Number);
          const dotHeight = height * 0.8;
          const dotScale = dotHeight / vbHeight;
          const dotWidth = vbWidth * dotScale;

          elements.push(
            `<path d="${
              characterShapes.dot.path
            }" fill="${textColor}" transform="translate(${
              x + (width - dotWidth) / 2
            },${y + height}) scale(${dotScale},${-dotScale})" />`
          );
        } else if (block.character === "-") {
          const [, , vbWidth, vbHeight] = characterShapes.dash.viewBox
            .split(" ")
            .map(Number);
          const dashWidth = width * 0.75;
          const dashHeight = height;
          const scaleX = dashWidth / vbWidth;
          const scaleY = dashHeight / vbHeight;

          elements.push(
            `<path d="${
              characterShapes.dash.path
            }" fill="${textColor}" transform="translate(${
              x + (width - dashWidth) / 2
            },${
              y + height / 2 - dashHeight / 12
            }) scale(${scaleX},${scaleY})" />`
          );
        } else if (block.character === " ") {
          // For space characters, don't render anything - just leave it as a gap
          // Remove the underscore line rendering
        }
      } else {
        const blockKey = block.character.toLowerCase() as Characters;
        const shape = characterShapes[blockKey];

        if (shape) {
          const [, , vbWidth, vbHeight] = shape.viewBox.split(" ").map(Number);
          elements.push(
            `<path d="${
              shape.path
            }" fill="${textColor}" transform="translate(${x},${y}) scale(${
              width / vbWidth
            },${height / vbHeight})" />`
          );
        }
      }
    }

    return elements.join("\n");
  };

  const rootBlock = blocks[0];
  const width = rootBlock.width;
  const height = rootBlock.height;

  // Generate SVG content (now with async support)
  const floatingBlocksSvg = renderFloatingBlocksToSVG(hiVis, width);
  const svgOverlaysSvg = await renderSvgOverlaysToSVG(hiVis, width);

  const svgContent = `<?xml version="1.0" encoding="UTF-8" standalone="no"?>
<svg 
  width="${width}mm"
  height="${height}mm"
  viewBox="0 0 ${width} ${height}"
  version="1.1"
  xmlns="http://www.w3.org/2000/svg"
  xmlns:xlink="http://www.w3.org/1999/xlink"
>
  ${renderBlockToSVG(rootBlock, 0, 0, width, height, 0, hiVis, undefined, undefined, false, undefined)}
  ${floatingBlocksSvg}
  ${svgOverlaysSvg}
</svg>`;

  return svgContent;
};

const DownloadDesign = ({ hiVis }: DownloadDesignProps) => {
  const handleDownload = async () => {
    if (!hiVis) return;

    // Generate SVG content using the standalone function
    const svgContent = await generateHiVisSVG(hiVis);

    if (!svgContent) return;

    // Create a Blob containing the SVG content
    const blob = new Blob([svgContent], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);

    // Create a temporary link and trigger download
    const link = document.createElement("a");
    link.href = url;
    link.download = `${hiVis.assetNumber || "design"}.svg`;
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Button onClick={handleDownload} className='w-full' variant='outline'>
      <ArrowDownToLine className='h-4 w-4' />
    </Button>
  );
};

export default DownloadDesign;
